import { API_PREFIX } from '@shared/api';
import { getToken } from '@/state/auth';

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

/**
 * Typed API client. Prepends the versioned prefix, attaches the bearer token
 * when present, and normalises the server's error envelope into ApiError.
 */
export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  let url: string;
  if (path.startsWith('http')) url = path;
  else if (path.startsWith(API_PREFIX)) url = path; // already fully-qualified (ROUTES.*)
  else url = `${API_PREFIX}${path}`;
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };
  const token = getToken();
  if (token) headers['authorization'] = `Bearer ${token}`;
  if (opts.body !== undefined) headers['content-type'] = 'application/json';

  const res = await fetch(url, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? safeJson(text) : null;

  if (!res.ok) {
    const err = (data as { error?: { code?: string; message?: string; details?: unknown } })?.error;
    throw new ApiError(err?.code ?? 'ERROR', err?.message ?? res.statusText, res.status, err?.details);
  }
  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/** Relative path helper (prefix is added by api()). */
export const p = (path: string): string => path.replace(API_PREFIX, '');
